import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageTitle, Badge, CHANNELS } from "@/components/app/ui";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarPage() {
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [f, setF] = useState({ topics: "", channels: ["LinkedIn", "Instagram"], start_date: ymd(new Date()), posts_per_week: 4, model: "auto" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/app/contents").then((r) => setItems(r.data.filter((x) => x.scheduled_for)));
  useEffect(() => { load(); }, []);

  const gen = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/app/ai/calendar", f);
      toast.success(`${data.items.length} ideas added · ${data.credits_charged} credits used`);
      load(); refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const days = useMemo(() => {
    const start = new Date(month); start.setDate(1 - ((start.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month]);
  const byDay = useMemo(() => items.reduce((a, x) => { const k = x.scheduled_for.slice(0, 10); (a[k] = a[k] || []).push(x); return a; }, {}), [items]);
  const toggleCh = (c) => setF({ ...f, channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c] });

  return (
    <div data-testid="calendar-page">
      <PageTitle title="Content Calendar" copy="Generate a two-week plan with AI, then turn ideas into drafts from Approvals." />
      <div className="grid xl:grid-cols-12 gap-6">
        <form onSubmit={gen} className="xl:col-span-4 card-box p-6 space-y-4 h-fit">
          <div className="space-y-2"><Label>Themes & topics</Label><Textarea required rows={3} value={f.topics} onChange={(e) => setF({ ...f, topics: e.target.value })} placeholder="e.g., chiller launch, energy-saving tips, plant case studies" data-testid="cal-topics-input" /></div>
          <div className="space-y-2"><Label>Channels</Label>
            <div className="flex flex-wrap gap-2">{CHANNELS.slice(0, 6).map((c) => (
              <button type="button" key={c} onClick={() => toggleCh(c)} data-testid={`cal-channel-${c.toLowerCase()}`} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${f.channels.includes(c) ? "bg-[#4f46e5] text-white border-[#4f46e5]" : "hover:bg-foreground/5"}`}>{c}</button>
            ))}</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Start date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="cal-start-input" /></div>
            <div className="space-y-2"><Label>Posts / week</Label><Input type="number" min={1} max={14} value={f.posts_per_week} onChange={(e) => setF({ ...f, posts_per_week: Number(e.target.value) })} data-testid="cal-ppw-input" /></div>
          </div>
          <div className="space-y-2"><Label>AI model</Label>
            <Select value={f.model} onValueChange={(v) => setF({ ...f, model: v })}><SelectTrigger data-testid="cal-model-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="auto">Auto · lowest cost</SelectItem><SelectItem value="gemini">Gemini 3 Flash · 50% credits</SelectItem><SelectItem value="claude">Claude Sonnet 4.5</SelectItem></SelectContent></Select>
          </div>
          <button disabled={busy || !f.channels.length} className="btn-brand w-full disabled:opacity-60" data-testid="cal-generate-btn"><Sparkles className="h-4 w-4" />{busy ? "Planning..." : "Generate 2-week plan · 15–30 credits"}</button>
        </form>
        <div className="xl:col-span-8 card-box p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold" data-testid="cal-month-label">{month.toLocaleString(undefined, { month: "long", year: "numeric" })}</h3>
            <div className="flex gap-2">
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="h-9 w-9 rounded-full border inline-flex items-center justify-center" data-testid="cal-prev-btn"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="h-9 w-9 rounded-full border inline-flex items-center justify-center" data-testid="cal-next-btn"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1.5 text-xs">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-2 py-1 font-mono text-[10px] uppercase text-muted-foreground">{d}</div>)}
            {days.map((d) => {
              const k = ymd(d); const list = byDay[k] || [];
              return (
                <div key={k} className={`min-h-[96px] rounded-lg border p-1.5 ${d.getMonth() !== month.getMonth() ? "opacity-40" : ""} ${k === ymd(new Date()) ? "border-[#7c3aed]" : ""}`}>
                  <div className="text-[11px] font-semibold mb-1">{d.getDate()}</div>
                  <div className="space-y-1">{list.slice(0, 3).map((x) => (
                    <div key={x.id} title={x.title} className="truncate rounded-md bg-[#4f46e5]/10 px-1.5 py-1 text-[10px] text-[#4f46e5] dark:text-[#a5b4fc]">{x.channel}: {x.title}</div>
                  ))}{list.length > 3 && <div className="text-[10px] text-muted-foreground">+{list.length - 3} more</div>}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground"><Badge s="planned" /><Badge s="scheduled" /><span>Open Approvals to draft planned ideas.</span></div>
        </div>
      </div>
    </div>
  );
}
